import { useId, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from './cn';

/**
 * Token-first single-select radio group rendered as selectable cards / a segmented
 * control (ADR-015 amendment 2026-07-07). Retains `role="radiogroup"` + native
 * `role="radio"` semantics (the native input is a visually-hidden `peer`; the card is
 * its sibling styled via `peer-checked`/`peer-focus-visible`), so it is far richer and
 * more touch-friendly than bare radios while staying WCAG 2.2 AA. Big tap target
 * (`min-h-11`), visible token focus ring, hover feedback. No raw hex/px.
 */
export interface RadioOption<V extends string> {
  value: V;
  label: ReactNode;
  description?: ReactNode;
  /** Optional decorative leading icon. */
  icon?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<V extends string> {
  /** Shared `name` for the native radios. */
  name: string;
  /** Visible group label. */
  legend: ReactNode;
  /** Accessible name for the `role="radiogroup"` container. */
  ariaLabel: string;
  value: V;
  onChange: (value: V) => void;
  options: ReadonlyArray<RadioOption<V>>;
  /**
   * Card layout. `inline` (default) is a responsive wrapping row where each card
   * takes a minimum width; `stacked` is a full-width vertical column where each
   * card hugs its content height (no phantom `basis-40` minimum height).
   */
  orientation?: 'inline' | 'stacked';
  /** Layout for the option cards (defaults to a responsive wrapping row). */
  optionsClassName?: string;
  className?: string;
  legendClassName?: string;
  /** Visible help under the legend, associated with the radiogroup. */
  description?: ReactNode;
}

export function RadioGroup<V extends string>({
  name,
  legend,
  ariaLabel,
  value,
  onChange,
  options,
  orientation = 'inline',
  optionsClassName,
  className,
  legendClassName,
  description,
}: RadioGroupProps<V>) {
  const groupId = useId();
  const descriptionId = `${groupId}-desc`;
  const isStacked = orientation === 'stacked';
  return (
    <fieldset className={cn('m-0 border-0 p-0', className)}>
      <legend className={cn('text-sm font-medium text-text-primary', legendClassName)}>
        {legend}
      </legend>
      {description ? (
        <p id={descriptionId} className="m-0 mt-1 text-sm text-text-secondary">
          {description}
        </p>
      ) : null}
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        aria-describedby={description ? descriptionId : undefined}
        className={cn('mt-2 flex gap-2', isStacked ? 'flex-col' : 'flex-wrap', optionsClassName)}
      >
        {options.map((option) => {
          const optionId = `${groupId}-${option.value}`;
          return (
            <label
              key={option.value}
              htmlFor={optionId}
              className={cn(
                'relative block',
                isStacked ? 'w-full' : 'flex-1 basis-40',
                option.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              <input
                id={optionId}
                type="radio"
                name={name}
                value={option.value}
                checked={value === option.value}
                disabled={option.disabled}
                onChange={() => onChange(option.value)}
                className="peer sr-only"
              />
              <span
                className={cn(
                  'flex min-h-11 items-start gap-3 rounded-md border border-border bg-surface p-3 transition-colors ease-ds',
                  'hover:bg-muted',
                  'peer-checked:border-primary peer-checked:bg-muted peer-checked:ring-1 peer-checked:ring-primary',
                  'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2',
                  'peer-disabled:opacity-60',
                )}
              >
                {option.icon ? (
                  <span aria-hidden="true" className="mt-0.5 flex shrink-0 items-center text-text-secondary">
                    {option.icon}
                  </span>
                ) : null}
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-sm font-medium text-text-primary">{option.label}</span>
                  {option.description ? (
                    <span className="text-sm text-text-secondary">{option.description}</span>
                  ) : null}
                </span>
              </span>
              <Check
                aria-hidden
                focusable={false}
                className="pointer-events-none absolute right-3 top-3 h-4 w-4 text-primary opacity-0 transition-opacity ease-ds peer-checked:opacity-100"
              />
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
